import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";

/**
 * What the workflow tasks dashboard can do for the AI omnibar.
 *
 * Its patient and caregiver pickers take ids from a typeahead rather than a list, so they are left
 * out: turning a name into one of those ids would mean guessing which person was meant. The task,
 * workflow, skill and cluster selects are lists, but they are the workflow engine's own vocabulary
 * and are not offered as options here - the page fetches them lazily, and an empty list would turn a
 * real filter into a free-text search.
 */
declareAiCapability({
  id: "workflowTasks.filter",
  title: "Filter the workflow tasks list",
  effect: "prefill",
  description:
    "Sets the filters on Workflows > Tasks: the created date range, the task's owner and its assignee. The page opens on tasks you own. Only works while that page is open.",
  location: "the filter bar of the Workflows > Tasks page",
  scope: "page",
  permissionKey: "view_workflow_task",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "Earliest created date to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Latest created date to include, as yyyy-MM-dd.",
    },
    {
      name: "owner",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to tasks owned by these agency members.",
    },
    {
      name: "assignee",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to tasks assigned to these agency members.",
    },
  ],
  examples: [
    "workflow tasks assigned to smith",
    "workflow tasks created last week",
    "tasks owned by jane",
  ],
});

function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("workflowTasks.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

export function useWorkflowTasksAiCapabilities(params: {
  /** The page's own setFilter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;
  const { queries } = useApi();

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  const agencyMembers = useQuery(queries.agencyMember.list());
  const members = React.useMemo(
    () =>
      (agencyMembers.data?.agencyMembers ?? []).map((member) => ({
        id: member.id,
        name: [member.firstName, member.lastName].filter(Boolean).join(" "),
      })),
    [agencyMembers.data?.agencyMembers]
  );

  useContributedOptions("owner", members);
  useContributedOptions("assignee", members);

  useProvideAiCapability(
    "workflowTasks.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.from !== undefined) {
        setFilterRef.current("createdAtFrom", String(args.from));
      }

      if (args.to !== undefined) {
        setFilterRef.current("createdAtTo", String(args.to));
      }

      if (args.from !== undefined || args.to !== undefined) {
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      // These two are `{ in: [...] }` filters rather than plain arrays, which is the shape the
      // page's own selects write.
      for (const [arg, key] of [
        ["owner", "ownerId"],
        ["assignee", "assigneeId"],
      ] as const) {
        if (args[arg] === undefined) {
          continue;
        }

        setFilterRef.current(key, { in: asList(args[arg]).map((one) => Number(one)) });
        applied.push(label(arg));
      }

      return applied.length === 0
        ? "Re-applied the workflow task filters"
        : `Showing workflow tasks: ${applied.join(", ")}`;
    },
    { enabled: agencyMembers.isSuccess }
  );
}
